import { type FC, type ReactNode } from "react";

import { ArrowRight, X } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { FieldError } from "@/shared/components/ui/FieldError";
import { SecurityNote } from "@/shared/components/ui/security-note";

interface SignatureStepContentProps {
  header: ReactNode;
  stepLabel: string;
  title: string;
  body: ReactNode;
  fullName: string;
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  nameError?: string;
  isSubmitting: boolean;
  isLastStep: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

export const SignatureStepContent: FC<SignatureStepContentProps> = ({
  header,
  stepLabel,
  title,
  body,
  fullName,
  onNameChange,
  nameError,
  isSubmitting,
  isLastStep,
  onSubmit,
  onClose,
}) => {
  const buttonText = isLastStep ? "Sign and finish" : "Sign and continue";

  return (
    <>
      <IconButton
        onClick={onClose}
        className="absolute right-6 top-6"
        label="Close"
        tooltip={false}
      >
        <X />
      </IconButton>

      {header}

      <div className="flex flex-col gap-2 w-full">
        <p className="text-sm text-secondary">{stepLabel}</p>
        <h3>{title}</h3>
      </div>

      <div className="flex flex-col gap-3 w-full">{body}</div>

      <div className="flex gap-6 items-center w-full">
        <div className="flex flex-1 flex-col gap-1.5 min-w-0">
          <Label htmlFor="tpa-legal-name">Type your legal name</Label>
          <Input
            id="tpa-legal-name"
            type="text"
            value={fullName}
            onChange={onNameChange}
            placeholder="Enter your full legal name"
            className={nameError ? "border-destructive" : ""}
            disabled={isSubmitting}
          />
          <FieldError message={nameError} />
        </div>

        <div className="flex flex-1 flex-col min-w-0 rounded-lg border border-primary overflow-hidden">
          <div className="flex items-center justify-center border-b border-primary py-2">
            <p className="text-sm font-medium text-tertiary">Signature preview</p>
          </div>
          <div className="p-4">
            <div className="relative h-8 w-full">
              <span className="absolute left-0 top-[12.72px] text-sm font-medium leading-[14px] text-primary">
                x
              </span>
              <span
                className="absolute left-[17px] top-0.5 text-base leading-7 text-primary"
                style={{ fontFamily: "Homemade Apple, cursive" }}
              >
                {fullName}
              </span>
              <div className="absolute bottom-0 left-0 right-0 border-b border-primary" />
            </div>
          </div>
        </div>
      </div>

      <Button
        variant="brand"
        size="md"
        rounded
        className="w-[400px] px-6 py-2"
        onClick={onSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Signing..." : buttonText}
        {!isSubmitting && <ArrowRight className="h-4 w-4" />}
      </Button>

      <SecurityNote text="Stored in your Documents. View or revoke your authorization at any time." />
    </>
  );
};

interface SignatureStepLayoutProps extends SignatureStepContentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modalRef: React.RefObject<HTMLDivElement | null>;
}

export const SignatureStepLayout: FC<SignatureStepLayoutProps> = ({
  open,
  onOpenChange,
  modalRef,
  ...contentProps
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        className="!p-0 !gap-0 overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Third-Party Authorization</DialogTitle>

        <div ref={modalRef} className="flex flex-col items-center gap-6 p-8">
          <SignatureStepContent {...contentProps} onClose={() => onOpenChange(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
